<script lang="ts">
    import { onMount } from 'svelte';
    import { fly, fade } from 'svelte/transition';
    import { activateIntegrations } from '$lib/global/events/loadAnalytics';
    import { getActiveIntegrations } from './helper';
    import {
        setCookiePreferences,
        getCookiePreferences,
        getCookiePreferencesPresent,
    } from '$lib/helper';
    import { trackCookies } from '$lib/global/tracking';
    import isEmpty from 'lodash/isEmpty';

    // Types
    import type { ComponentType } from 'svelte';
    import type { Language } from '$lib/types/Language';
    import type { IntegrationData } from '$lib/components/ConsentOverlay/types';

    export let layout = 'none';
    export let language: Language = 'de';
    export let campaignId = '';
    export let legalLink = '';
    export let termsLink = '';
    export let integrations: IntegrationData = {};
    export let forceVisible = false;
    export let disableCookiePoliciesLink = false;
    export let onAcceptClick: (() => void) | null = null;

    // Get existing preferences
    let preferencesPresent = false;
    let mounted = false;
    let preferences = {};
    let accepted = false;

    let Banner: ComponentType;

    onMount(async () => {
        if (layout === 'normal') {
            Banner = (await import('./Normal/Normal.svelte')).default;
        } else if (layout === 'explicit') {
            Banner = (await import('./Explicit/Explicit.svelte')).default;
        } else if (layout === 'none') {
            activateIntegrations(integrations);
        }

        preferencesPresent = getCookiePreferencesPresent(campaignId);
        preferences = getCookiePreferences(campaignId);
        // only use the analytics if the user has not already accepted the cookies
        if (!isEmpty(preferences) && !forceVisible) {
            acceptCookies(preferences);
        }

        mounted = true;
    });

    const acceptCookies = (selectedIntegrations: IntegrationData) => {
        accepted = true;
        setCookiePreferences(campaignId, selectedIntegrations);

        // Tracking
        const activeIntegrations = getActiveIntegrations(selectedIntegrations, integrations);
        const integrationNames = Object.keys(activeIntegrations).filter(
            (integration) => integration !== 'Perspective',
        );
        // tracking events are queued, so it can be called before activating integrations
        trackCookies(integrationNames);

        activateIntegrations(activeIntegrations);

        if (onAcceptClick) {
            onAcceptClick();
        }
    };

    $: showOverlay =
        (mounted && !preferencesPresent && !accepted && layout !== 'none') || forceVisible;
</script>

{#if showOverlay}
    <div class="fixed top-0 left-0 z-[999] h-screen w-screen bg-black/20" transition:fade>
        <div class="flex h-full w-full justify-center sm:items-center" transition:fly={{ y: 50 }}>
            <svelte:component
                this={Banner}
                integrationsToShow={Object.keys(integrations)}
                storedIntegrations={preferences}
                {acceptCookies}
                {legalLink}
                {termsLink}
                {language}
                {disableCookiePoliciesLink}
            />
        </div>
    </div>
{/if}
